import { useEffect, useRef } from 'react';
import { toast } from '@/lib/toast';
import { useAutomationSocket } from '@/hooks/useAutomationSocket';
import { useAutomationPolling } from '@/hooks/useAutomationPolling';
import { useAuthStore } from '@/stores';
import { onboardingApi } from '@/services';

/** Backoff between re-checks of the guard's own flag, in ms. */
const CONFIRM_DELAYS_MS = [0, 1_000, 2_000, 4_000, 8_000, 8_000];

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Headless component mounted in AppLayout to keep the automation socket
 * subscription alive after the user skips to the app. Updates the store
 * so BackgroundProcessIndicator can read from it, and fires success/error
 * toasts on completion. (HIV-112, LIL-219)
 */
export function AutomationProgressTracker() {
  const { isComplete, error } = useAutomationSocket();
  const { setHasCompletedOnboarding, setHasSkippedOnboarding } = useAuthStore();
  const completedRef = useRef(false);

  // HTTP polling fallback so completion is detected even when the socket
  // never connects after skip.
  useAutomationPolling(true);

  // Unlock the app when automation finishes — but only once the backend agrees.
  //
  // The global OnboardingGuard keys off `onboarding.completed`, which the
  // pipeline writes at the very END of its run. The automation status therefore
  // reads "completed" a beat before the guard does, and unlocking on that alone
  // ungated every page while the BE was still answering 403 ONBOARDING_INCOMPLETE
  // — the user got "An unexpected error occurred" instead of a setup card
  // (LIL-440). Re-check the guard's own flag, with backoff, before flipping.
  useEffect(() => {
    if (!isComplete || completedRef.current) return;
    completedRef.current = true;

    let cancelled = false;

    async function unlockWhenBackendAgrees() {
      for (const delay of CONFIRM_DELAYS_MS) {
        if (delay) await wait(delay);
        if (cancelled) return;
        try {
          const status = await onboardingApi.getOnboardingStatus();
          if (cancelled) return;
          if (status.completed) {
            setHasCompletedOnboarding(true);
            setHasSkippedOnboarding(false);
            toast.success('Your AI workspace is ready!', {
              id: 'automation-progress',
              duration: 4000,
            });
            return;
          }
        } catch {
          // Network hiccup — fall through to the next attempt.
        }
      }
      // Still not confirmed. Stay gated and let the next app entry re-sync,
      // rather than unlocking into a wall of 403s.
      completedRef.current = false;
    }

    void unlockWhenBackendAgrees();
    return () => {
      cancelled = true;
    };
  }, [isComplete, setHasCompletedOnboarding, setHasSkippedOnboarding]);

  // Show error toast
  useEffect(() => {
    if (error) {
      toast.error(`Setup issue: ${error.message}`, {
        id: 'automation-progress',
        duration: 8000,
      });
    }
  }, [error]);

  return null;
}
